// Builds Effect Schemas from shape definitions, for answer rule 5. The
// definitions come from WarmHub at run time, by pinned shape ref (see
// services/shape-defs.ts), or from the toolset's manifest, so nothing here
// knows any hub's shapes.

import { Schema } from "effect";
import type { FieldDef, ShapeDef } from "./defs.ts";
import { Json } from "./json.ts";

// WarmHub reads null as an absent value only for a field of an object
// (@warmhub/sdk-ts 0.148.0, dist/chunk-IXJEQEPN.js, `ai` and `li`): a required
// field with null is missing, and an optional field may be null. An array's
// element goes to its type's own check, which for a type WarmHub doesn't have
// accepts any value, null too.
const notNull = Schema.makeFilter((v: Json) => v !== null || "Expected a value, not null");
const isObject = Schema.makeFilter((v) => (typeof v === "object" && v !== null && !Array.isArray(v)) || "Expected object");

function fieldsSchema(fields: Readonly<Record<string, FieldDef>>): Schema.Top {
  const schemas = Object.fromEntries(Object.entries(fields).map(([name, f]) => {
    if (f.optional) return [name, Schema.optionalKey(Schema.NullOr(fieldSchema(f)))];
    return [name, SCHEMAS.has(f.type) ? fieldSchema(f) : Json.check(notNull)];
  }));
  // A Struct with no fields accepts any value, but WarmHub reads {} as an object.
  return Schema.Struct(schemas).check(isObject);
}

function stringSchema(f: FieldDef): Schema.Top {
  const members = f.enum;
  let s: Schema.String = Schema.String;
  if (f.minLength !== undefined) s = s.check(Schema.isMinLength(f.minLength));
  if (f.maxLength !== undefined) s = s.check(Schema.isMaxLength(f.maxLength));
  return members === undefined ? s : s.check(Schema.makeFilter((v: string) => members.includes(v) || `Expected one of ${members.map((m) => JSON.stringify(m)).join(", ")}`));
}

function numberSchema(f: FieldDef): Schema.Top {
  let n: Schema.Number = Schema.Number;
  if (f.minimum !== undefined) n = n.check(Schema.isGreaterThanOrEqualTo(f.minimum));
  if (f.maximum !== undefined) n = n.check(Schema.isLessThanOrEqualTo(f.maximum));
  return f.integer ? n.check(Schema.makeFilter((v: number) => Number.isInteger(v) || "Expected an integer")) : n;
}

function arraySchema(f: FieldDef): Schema.Top {
  let a = Schema.Array(f.items ? fieldSchema(f.items) : Schema.Unknown);
  if (f.minItems !== undefined) a = a.check(Schema.isMinLength(f.minItems));
  if (f.maxItems !== undefined) a = a.check(Schema.isMaxLength(f.maxItems));
  return a;
}

const SCHEMAS = new Map<string, (f: FieldDef) => Schema.Top>([
  ["string", stringSchema], ["wref", () => Schema.String], ["boolean", () => Schema.Boolean],
  ["number", numberSchema], ["array", arraySchema], ["object", (f) => fieldsSchema(f.fields ?? {})],
]);

// A field type WarmHub doesn't have, or one the definition didn't give, accepts any value.
function fieldSchema(f: FieldDef): Schema.Top {
  return SCHEMAS.get(f.type)?.(f) ?? Json;
}

// What validation reads of a definition, a WarmHub shape's or a manifest shape's.
type ShapeFields = Pick<ShapeDef, "fields">;

// A definition's schema is built once per definition object, which each hub
// reads and caches for itself, so no hub's validation uses another hub's
// cached definition. See Design > Operations, "Caches".
const cache = new WeakMap<ShapeFields, Schema.Top>();

function schemaForDef(def: ShapeFields): Schema.Top {
  const cached = cache.get(def);
  if (cached) return cached;
  const schema = fieldsSchema(def.fields);
  cache.set(def, schema);
  return schema;
}

/**
 * Splits a pinned shape ref: "wh:org/repo/Shape/Name@v2" -> `{ repo:
 * "org/repo", name: "Name", version: 2 }`. Undefined for anything else,
 * including an unpinned ref.
 */
export function parseShapeRef(ref: string): { repo: string; name: string; version: number } | undefined {
  const m = /^wh:([^/]+\/[^/]+)\/Shape\/([^@]+)@v(\d+)$/.exec(ref);
  const repo = m?.[1];
  const name = m?.[2];
  return repo && name ? { repo, name, version: Number(m?.[3]) } : undefined;
}

/**
 * A pinned shape ref's key without its version, as an intent's answer shapes
 * name it: "wh:org/repo/Shape/Name@v2" -> "org/repo/Name". Undefined for
 * anything that isn't a pinned shape ref.
 */
export function shapeKeyFromRef(ref: string): string | undefined {
  const p = parseShapeRef(ref);
  return p ? `${p.repo}/${p.name}` : undefined;
}

/**
 * Whether `ref` is a manifest shape's name, such as "Quote": a letter, then
 * letters, digits or underscores. An answer names a manifest shape by it,
 * with no repository or version. See Design > Answer shapes, "Manifest shapes".
 */
export const isShapeName = (ref: string): boolean => /^[A-Za-z][A-Za-z0-9_]*$/.test(ref);

/**
 * Decodes data against a shape definition, a WarmHub shape's or a manifest
 * shape's (Design > Answer shapes, rule 5). Returns a list of problems, at
 * most one message of up to 300 characters; empty means valid. Fields the
 * definition doesn't name are allowed.
 */
export function checkShapeDef(def: ShapeFields, data: Json): string[] {
  // SAFETY: schemaForDef builds only from Struct, Array, NullOr, Json, String,
  // Number, Boolean, Unknown and filters, which decode without services.
  // Verified by: test/answer.test.ts, "accepts a valid assertion about a
  // Thing, checked against the definition WarmHub gives".
  const result = Schema.decodeUnknownResult(schemaForDef(def) as Schema.Codec<unknown, unknown>)(data);
  return result._tag === "Success" ? [] : [String(result.failure.message).slice(0, 300)];
}
